import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { IUserDetails } from "..";
import {
	CONTACT_NUMBER_REGEX,
	PASSWORD_REGEX,
	USER_ROLE_ADMIN_ID,
	USER_ROLE_STAFF_ID,
	USER_ROLE_TYPES_E
} from "../../../../utils/constants";
import { useCreateUserMutation, useEditUserMutation } from "../../../../redux/reducers/configuration.reducer";
import NumberInput from "../../../../common/NumberInput";
import { useGetSiteConfigQuery } from "../../../../redux/reducers/settings.reducer";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../../../utils/commonUtils";
import ChangePasswordDialog from "../ChangePasswordDialog";
import { useAppSelector } from "../../../../redux";

interface IUserDetailsDialogProps {
	open: boolean;
	userDetails: IUserDetails | null;
	onClose: () => void;
}

interface IUserFormInputs {
	username: string;
	contact_number: string;
	first_name: string;
	last_name: string;
	address: string;
	user_type: number;
	password: string;
	confirm_password: string;
}

const UserDetailsDialog: FC<IUserDetailsDialogProps> = (props) => {
	const { open, userDetails, onClose } = props;

	const authState = useAppSelector((state) => state.auth);

	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
	const [showChangePasswordDialog, setShowChangePasswordDialog] = useState<boolean>(false);

	const [
		createUser,
		{ isLoading: createUserLoading, isSuccess: createUserSuccess, error: createUserError, reset: resetCreateUserState }
	] = useCreateUserMutation();

	const [
		editUser,
		{ isLoading: editUserLoading, isSuccess: editUserSuccess, error: editUserError, reset: resetEditUserState }
	] = useEditUserMutation();

	const { data: siteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(siteConfig?.cloud_sync_enabled ?? true),
		[siteConfig]
	);

	const ReadOnlyInputs = useMemo<boolean>(
		() =>
			!!userDetails &&
			[USER_ROLE_ADMIN_ID, USER_ROLE_STAFF_ID].includes(userDetails.user_type) &&
			RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_ADMIN),
		[RestrictedAccess, userDetails]
	);

	const {
		register,
		reset,
		control,
		handleSubmit,
		watch,
		formState: { errors }
	} = useForm<IUserFormInputs>();

	const passwordInput = watch("password");

	function handleToggleShowPassword() {
		setShowPassword((currentShowPasswordState) => !currentShowPasswordState);
	}

	function handleToggleShowConfirmPassword() {
		setShowConfirmPassword((currentShowConfirmPasswordState) => !currentShowConfirmPasswordState);
	}

	function handleOpenChangePasswordDialog() {
		setShowChangePasswordDialog(true);
	}

	function handleCloseChangePasswordDialog() {
		setShowChangePasswordDialog(false);
	}

	function handleSubmitForm(data: IUserFormInputs) {
		if (userDetails) {
			editUser({
				id: userDetails.id,
				data: {
					first_name: data.first_name,
					last_name: data.last_name,
					phone_number: data.contact_number,
					address: data.address,
					role: Number(data.user_type)
				}
			});
		} else {
			createUser({
				username: data.username,
				password: data.password,
				address: data.address,
				phone_number: data.contact_number,
				first_name: data.first_name,
				last_name: data.last_name,
				role: Number(data.user_type)
			});
		}
	}

	function getUserRoleVisibility(roleId: string) {
		if (ReadOnlyInputs) return true;

		if (!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_ADMIN)) return true;

		if (Number(roleId) !== USER_ROLE_ADMIN_ID) return true;

		return false;
	}

	useEffect(() => {
		if (open) {
			if (userDetails) {
				reset({
					username: userDetails.username,
					contact_number: userDetails.contact_number,
					first_name: userDetails.first_name,
					last_name: userDetails.last_name,
					address: userDetails.address,
					user_type: userDetails.user_type,
					password: "",
					confirm_password: ""
				});
			} else {
				reset({
					username: "",
					contact_number: "",
					first_name: "",
					last_name: "",
					address: "",
					user_type: 0,
					password: "",
					confirm_password: ""
				});
			}
		} else {
			setShowPassword(false);
			setShowConfirmPassword(false);
			resetCreateUserState();
			resetEditUserState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, reset, userDetails]);

	useEffect(() => {
		if (createUserSuccess) {
			onClose();
		}
	}, [createUserSuccess, onClose]);

	useEffect(() => {
		if (editUserSuccess) {
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editUserSuccess, onClose]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle
				textAlign="center"
				sx={{
					background: "#5b4ebf",
					color: "white",
					position: "relative",
					padding: "16px 20px 12px 20px",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 600 }}>
						User Details
					</Typography>
				</Box>
				<Box sx={{ position: "absolute", top: 12, right: 12 }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: "6px",
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				classes={{ root: "dialog-content-root" }}
				sx={{
					background: "#ffffff"
				}}
			>
				<Grid container spacing={2} component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="first-name-input"
							error={!!errors.first_name}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							First Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="first-name-input"
							placeholder="Enter the first name"
							autoComplete="off"
							error={!!errors.first_name}
							helperText={errors.first_name && errors.first_name.message ? errors.first_name.message.toString() : ""}
							InputProps={{ readOnly: ReadOnlyInputs }}
							{...register("first_name", { required: "First name is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="last-name-input"
							error={!!errors.last_name}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Last Name
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="last-name-input"
							placeholder="Enter the last name"
							autoComplete="off"
							error={!!errors.last_name}
							helperText={errors.last_name && errors.last_name.message ? errors.last_name.message.toString() : ""}
							InputProps={{ readOnly: ReadOnlyInputs }}
							{...register("last_name", { required: "Last name is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="username-input"
							error={!!errors.username}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Username
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="username-input"
							placeholder="Enter the username"
							autoComplete="off"
							error={!!errors.username}
							helperText={errors.username && errors.username.message ? errors.username.message.toString() : ""}
							InputProps={{ readOnly: !!userDetails || ReadOnlyInputs }}
							{...register("username", { required: "Username is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="contact-number-input"
							error={!!errors.contact_number}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Contact Number
						</InputLabel>

						<NumberInput
							required
							fullWidth
							positiveOnly
							disableSigned
							disableDecimal
							isContactNumber
							variant="outlined"
							color="primary"
							size="small"
							id="contact-number-input"
							placeholder="Enter the contact number"
							error={!!errors.contact_number}
							helperText={
								errors.contact_number && errors.contact_number.message ? errors.contact_number.message.toString() : ""
							}
							InputProps={{ readOnly: ReadOnlyInputs }}
							{...register("contact_number", {
								required: "Contact number is required",
								pattern: { value: CONTACT_NUMBER_REGEX, message: "Enter a valid contact number" },
								min: { value: 0, message: "Invalid contact number" }
							})}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="address-input"
							error={!!errors.address}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							Address
						</InputLabel>

						<TextField
							required
							fullWidth
							variant="outlined"
							color="primary"
							size="small"
							id="address-input"
							placeholder="Enter the Address"
							autoComplete="off"
							error={!!errors.address}
							helperText={errors.address && errors.address.message ? errors.address.message.toString() : ""}
							InputProps={{ readOnly: ReadOnlyInputs }}
							{...register("address", { required: "Address is required" })}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<InputLabel
							required
							htmlFor="user-type-input"
							error={!!errors.user_type}
							sx={{
								fontWeight: 600,
								color: "#3D3D3D",
								marginBottom: "0.5rem",
								fontSize: "0.875rem"
							}}
						>
							User Type
						</InputLabel>

						<Controller
							name="user_type"
							control={control}
							rules={{ required: "User type is required" }}
							render={({ field: { onChange, value } }) => (
								<FormControl required fullWidth variant="outlined" color="primary" size="small">
									<Select
										required
										fullWidth
										value={value}
										onChange={onChange}
										sx={{ height: "100%" }}
										error={!!errors.user_type}
										readOnly={ReadOnlyInputs}
									>
										{Object.entries(USER_ROLE_TYPES_E).map(([id, name]) =>
											getUserRoleVisibility(id) ? (
												<MenuItem value={id} key={id}>
													{name}
												</MenuItem>
											) : null
										)}
									</Select>

									{errors.user_type && errors.user_type.message ? (
										<FormHelperText>{errors.user_type.message.toString()}</FormHelperText>
									) : null}
								</FormControl>
							)}
						/>
					</Grid>

					{!userDetails ? (
						<>
							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="password-input"
									error={!!errors.password}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: "0.875rem"
									}}
								>
									Password
								</InputLabel>

								<TextField
									required
									fullWidth
									type={showPassword ? "text" : "password"}
									autoComplete="new-password"
									variant="outlined"
									color="primary"
									size="small"
									id="password-input"
									placeholder="Enter password"
									error={!!errors.password}
									helperText={errors.password && errors.password.message ? errors.password.message.toString() : ""}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton size="small" onClick={handleToggleShowPassword} tabIndex={-1}>
													{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
												</IconButton>
											</InputAdornment>
										)
									}}
									{...register("password", {
										required: !userDetails ? "Password is required" : false,
										pattern: {
											value: PASSWORD_REGEX,
											message: "Password should contain at least one alphabet, one number and a special character"
										},
										minLength: {
											value: 8,
											message: "Password should be at least 8 characters long"
										}
									})}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
							<InputLabel
									required
									htmlFor="confirm-password-input"
									error={!!errors.confirm_password}
									sx={{
										fontWeight: 600,
										color: "#3D3D3D",
										marginBottom: "0.5rem",
										fontSize: "0.875rem"
									}}
								>
									Confirm Password
								</InputLabel>
								
								<TextField
									required
									fullWidth
									type={showConfirmPassword ? "text" : "password"}
									autoComplete="new-password"
									variant="outlined"
									color="primary"
									size="small"
									id="confirm-password-input"
									placeholder="Confirm Password"
									error={!!errors.confirm_password}
									helperText={
										errors.confirm_password && errors.confirm_password.message
											? errors.confirm_password.message.toString()
											: ""
									}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton size="small" onClick={handleToggleShowConfirmPassword} tabIndex={-1}>
													{showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
												</IconButton>
											</InputAdornment>
										)
									}}
									{...register("confirm_password", {
										required: !userDetails ? "Confirm password is required" : false,
										validate: (value) =>
											value !== passwordInput ? "Confirm password should be same as password" : true
									})}
								/>
							</Grid>
						</>
					) : null}

					{createUserError ? (
						<Grid item xs={12}>
							<Typography variant="body2" color="var(--color-error-main)" textAlign="center">
								{"data" in createUserError
									? `${String(createUserError.data).slice(0, 1).toUpperCase()}${String(createUserError.data).slice(1)}`
									: "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{editUserError ? (
						<Grid item xs={12}>
							<Typography variant="body2" color="var(--color-error-main)" textAlign="center">
								{"data" in editUserError
									? `${String(editUserError.data).slice(0, 1).toUpperCase()}${String(editUserError.data).slice(1)}`
									: "Something went wrong"}
							</Typography>
						</Grid>
					) : null}

					{!ReadOnlyInputs ? (
						<>
							{userDetails ? (
								<Grid item xs={6}>
									<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
										<Button
											disableElevation
											variant="outlined"
											color="primary"
											onClick={handleOpenChangePasswordDialog}
										>
											Change Password
										</Button>
									</Box>
								</Grid>
							) : null}

							<Grid item xs={userDetails ? 6 : 12}>
								<Box
									sx={{ display: "flex", alignItems: "center", justifyContent: userDetails ? "flex-start" : "center" }}
								>
									<LoadingButton
										disableElevation
										variant="contained"
										color="primary"
										sx={{ paddingX: 4 }}
										loading={userDetails ? editUserLoading : createUserLoading}
										type="submit"
									>
										Save
									</LoadingButton>
								</Box>
							</Grid>
						</>
					) : null}
				</Grid>
			</DialogContent>

			<ChangePasswordDialog
				open={showChangePasswordDialog}
				userId={userDetails?.id ?? -1}
				onClose={handleCloseChangePasswordDialog}
			/>
		</Dialog>
	);
};

export default UserDetailsDialog;
